import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AppShell } from "@/components/AppShell";
import { AUTH_STATUS_KEY, AuthGate } from "@/components/AuthGate";
import { CardDetailPage } from "@/routes/CardDetail";
import { CardsPage } from "@/routes/Cards";
import { DashboardPage } from "@/routes/Dashboard";
import { CashbackPage } from "@/routes/Cashback";
import { CyclesPage } from "@/routes/Cycles";
import { NotFoundPage } from "@/routes/Placeholder";
import { TransactionsPage } from "@/routes/Transactions";
import { SettingsPage } from "@/routes/Settings";
import { StatementsPage } from "@/routes/Statements";
import { StatusPage } from "@/routes/Status";

// A 401 from any call means the session expired or was signed out elsewhere.
// Re-ask the server, and the AuthGate puts the login screen back — rather than
// every page showing its own "Not authenticated." error.
function onApiError(error: unknown) {
  if ((error as { status?: number })?.status === 401) {
    void queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      // Financial data: don't silently serve stale numbers, but don't refetch
      // on every window focus either — the user is reading, not polling.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry a 4xx — the request was wrong, not unlucky.
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        <BrowserRouter>
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="cards/:id" element={<CardDetailPage />} />
              <Route path="cycles" element={<CyclesPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="cashback" element={<CashbackPage />} />
              <Route path="statements" element={<StatementsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              {/* Temporary — live build progress. Removed with the beta. */}
              <Route path="status" element={<StatusPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthGate>

      <Toaster
        position="bottom-right"
        toastOptions={{
          // Match the panel language rather than shipping sonner's defaults.
          className: "!bg-panel !border-hairline !text-ink !font-body",
        }}
      />
    </QueryClientProvider>
  );
}
